import React, { useMemo, useState } from "react";
import AdminLayout from "../../layouts/AdminLayout";
import AdminAccessDenied from "./AdminAccessDenied";
import DebugPanel from "../../components/admin/debug/DebugPanel";
import DebugRow from "../../components/admin/debug/DebugRow";
import EmptyState from "../../components/ui/EmptyState";
import { usePlatformContext, isFeatureEnabled } from "../../context/PlatformContext";
import { getFeatureStatus } from "../../utils/featureControl";
import { isAdminModuleEnabled } from "../../config/features";
import { safeParseJson, safeStringifyJson } from "../../utils/safeJson";

const STORAGE_KEY = "myv_platform_context";

const flattenFeatures = (features, prefix = "") => {
  if (!features || typeof features !== "object") return [];
  return Object.entries(features).flatMap(([key, value]) => {
    const nextKey = prefix ? `${prefix}.${key}` : key;
    if (value && typeof value === "object" && !Array.isArray(value)) {
      return flattenFeatures(value, nextKey);
    }
    return [{ key: nextKey, enabled: value === true }];
  });
};

const resolveAdminRole = (contextUser) => {
  const role = String(contextUser?.role || "").toUpperCase();
  const roles = Array.isArray(contextUser?.roles)
    ? contextUser.roles.map((item) => String(item).toUpperCase())
    : [];
  return role === "ADMIN" || roles.includes("ADMIN");
};

const buildRouteStatus = ({
  label,
  path,
  featureKey,
  uiFeatureKey,
  requireAdmin,
  features,
  contextStatus,
  user,
}) => {
  const reasons = [];
  if (contextStatus !== "ready") {
    reasons.push("Platform context missing");
  }
  if (requireAdmin && !isAdminModuleEnabled(user)) {
    reasons.push("Role mismatch");
  }
  if (featureKey && !isFeatureEnabled(features, featureKey)) {
    reasons.push("Feature flag disabled");
  }
  const uiStatus = uiFeatureKey ? getFeatureStatus(uiFeatureKey) : null;
  if (uiStatus && uiStatus.status === "disabled") {
    reasons.push("UI feature disabled");
  }
  if (uiStatus && uiStatus.status === "maintenance") {
    reasons.push("Maintenance mode");
  }
  const status =
    reasons.length === 0
      ? "Visible"
      : reasons.includes("Maintenance mode")
      ? "Gated"
      : "Hidden";

  return {
    label,
    path,
    status,
    reason: reasons.length ? reasons.join(", ") : "—",
  };
};

export default function AdminDebug() {
  const { features, status: contextStatus, user } = usePlatformContext();
  const storedContext = safeParseJson(
    typeof window !== "undefined" ? sessionStorage.getItem(STORAGE_KEY) : null
  );
  const isAdminContext = resolveAdminRole(storedContext?.user);
  const [copyNotice, setCopyNotice] = useState("");

  const flattenedFeatures = useMemo(
    () => flattenFeatures(features),
    [features]
  );

  const platformFeatureRows = useMemo(
    () =>
      flattenedFeatures.map((item) => ({
        key: item.key,
        enabled: item.enabled ? "Enabled" : "Disabled",
        source: "platform context",
        scope: "global",
      })),
    [flattenedFeatures]
  );

  const uiFeatureKeys = useMemo(
    () => [
      "dashboard",
      "dashboardWidgets",
      "clients",
      "properties",
      "analysis",
      "reports",
      "remedies",
      "prescriptions",
      "learn",
      "help",
      "saved-analyses",
      "insights",
      "recommendations",
      "workspaceLayouts",
      "settings",
      "profile",
      "billing",
      "payments",
      "usage",
      "analytics",
      "vastu.enabled",
      "vastu.tools",
    ],
    []
  );

  const uiFeatureRows = useMemo(
    () =>
      uiFeatureKeys.map((key) => {
        const status = getFeatureStatus(key);
        return {
          key,
          enabled: status.status === "enabled" ? "Enabled" : "Disabled",
          source: "ui_features.json (default)",
          scope: "page-level",
          status: status.status,
        };
      }),
    [uiFeatureKeys]
  );

  const routeRows = useMemo(() => {
    const adminRoutes = [
      { label: "Admin Overview", path: "/admin/overview", requireAdmin: true, featureKey: "analytics" },
      { label: "Admin Users", path: "/admin/users", requireAdmin: true },
      { label: "Admin Billing", path: "/admin/billing/transactions", requireAdmin: true },
      { label: "Admin Reports Status", path: "/admin/reports/status", requireAdmin: true },
      { label: "Admin Analysis Errors", path: "/admin/analysis/errors", requireAdmin: true },
      { label: "Admin Master Data", path: "/admin/master-data", requireAdmin: true },
      { label: "Admin System", path: "/admin/system", requireAdmin: true },
      { label: "Admin Debug", path: "/admin/debug", requireAdmin: true },
    ];
    const dashboardRoutes = [
      { label: "Dashboard Home", path: "/dashboard", featureKey: "dashboard" },
      { label: "Clients", path: "/dashboard/clients", featureKey: "clients" },
      { label: "Properties", path: "/dashboard/properties", featureKey: "properties" },
      { label: "Layout Setup", path: "/dashboard/layout", featureKey: "workspaceLayouts" },
      { label: "Directions", path: "/dashboard/directions", featureKey: "vastu.enabled" },
      { label: "Analysis", path: "/dashboard/analysis", featureKey: "analysis" },
      { label: "Reports", path: "/dashboard/reports", featureKey: "reports" },
      { label: "Remedies", path: "/dashboard/remedies", uiFeatureKey: "remedies" },
      { label: "Prescriptions", path: "/dashboard/prescriptions", uiFeatureKey: "prescriptions" },
      { label: "Learn", path: "/dashboard/learn", uiFeatureKey: "learn" },
      { label: "Help", path: "/dashboard/help", uiFeatureKey: "help" },
      { label: "Insights", path: "/dashboard/insights", uiFeatureKey: "insights" },
      { label: "Recommendations", path: "/dashboard/recommendations", uiFeatureKey: "recommendations" },
      { label: "Workspace", path: "/dashboard/workspace", featureKey: "workspaceLayouts" },
      { label: "Saved Analyses", path: "/dashboard/saved-analyses", uiFeatureKey: "saved-analyses" },
      { label: "Billing", path: "/dashboard/billing", featureKey: "billing" },
      { label: "Settings", path: "/dashboard/settings", featureKey: "settings" },
      { label: "Devta", path: "/dashboard/devta", featureKey: "vastu.tools" },
      { label: "Ayadi", path: "/dashboard/ayadi", featureKey: "vastu.tools" },
      { label: "Numerology", path: "/dashboard/numerology", featureKey: "vastu.tools" },
      { label: "Astro Vastu", path: "/dashboard/astro-vastu", featureKey: "vastu.tools" },
      { label: "Lal Kitab", path: "/dashboard/lal-kitab", featureKey: "vastu.tools" },
    ];
    return {
      admin: adminRoutes.map((route) =>
        buildRouteStatus({
          ...route,
          features,
          contextStatus,
          user,
        })
      ),
      dashboard: dashboardRoutes.map((route) =>
        buildRouteStatus({
          ...route,
          features,
          contextStatus,
          user,
        })
      ),
    };
  }, [contextStatus, features, user]);

  if (!isAdminContext) {
    return <AdminAccessDenied />;
  }

  const rawContextJson = safeStringifyJson(storedContext, "");

  return (
    <AdminLayout title="Admin Debug">
      <div className="space-y-6">
        <div className="card p-4 text-sm text-slate-600">
          Internal debug visibility for platform context, feature gates, and route access.
        </div>

        <DebugPanel
          title="Platform Context Inspector"
          description="Read-only values from sessionStorage: myv_platform_context."
          testId="admin-debug-context"
        >
          {!storedContext ? (
            <EmptyState
              title="No platform context available"
              message="Session storage does not contain a platform context payload."
              testId="admin-debug-empty-context"
            />
          ) : (
            <>
              <div className="rounded-lg border border-slate-100 p-4">
                <DebugRow label="User ID" value={storedContext?.user?.id} />
                <DebugRow label="User Email" value={storedContext?.user?.email} />
                <DebugRow
                  label="User Role"
                  value={storedContext?.user?.role || storedContext?.user?.roles?.join(", ")}
                />
                <DebugRow
                  label="Enabled Features"
                  value={
                    storedContext?.features
                      ? `${Object.keys(storedContext.features).length} keys`
                      : "—"
                  }
                />
                <DebugRow label="Environment" value={storedContext?.environment} />
                <DebugRow label="Tenant" value={storedContext?.tenant} />
                <DebugRow
                  label="Session Timestamp"
                  value={
                    storedContext?.session?.timestamp ||
                    storedContext?.session?.createdAt ||
                    storedContext?.session?.updatedAt
                  }
                />
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                <button
                  type="button"
                  className="btn btn-secondary text-xs"
                  onClick={() => {
                    if (!rawContextJson) return;
                    if (navigator?.clipboard?.writeText) {
                      navigator.clipboard.writeText(rawContextJson);
                      setCopyNotice("Platform context copied.");
                      setTimeout(() => setCopyNotice(""), 2000);
                    }
                  }}
                >
                  Copy JSON
                </button>
                {copyNotice && <span>{copyNotice}</span>}
              </div>
            </>
          )}
        </DebugPanel>

        <DebugPanel
          title="Feature Flag Visibility"
          description="View-only snapshot of feature flags and their sources."
          testId="admin-debug-features"
        >
          <div className="space-y-4">
            <div className="rounded-lg border border-slate-100 p-4">
              <p className="text-xs font-semibold text-slate-500">
                Platform Context Flags
              </p>
              {platformFeatureRows.length === 0 ? (
                <EmptyState
                  title="No platform flags available"
                  message="Platform feature flags are missing from context."
                  testId="admin-debug-empty-platform-flags"
                />
              ) : (
                <div className="mt-3 space-y-2 text-sm">
                  {platformFeatureRows.map((row) => (
                    <div
                      key={row.key}
                      className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 pb-2 text-xs text-slate-600"
                    >
                      <span className="font-semibold text-slate-700">{row.key}</span>
                      <span>{row.enabled}</span>
                      <span className="text-slate-500">{row.source}</span>
                      <span className="text-slate-400">{row.scope}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="rounded-lg border border-slate-100 p-4">
              <p className="text-xs font-semibold text-slate-500">UI Feature Flags</p>
              <div className="mt-3 space-y-2 text-sm">
                {uiFeatureRows.map((row) => (
                  <div
                    key={row.key}
                    className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 pb-2 text-xs text-slate-600"
                  >
                    <span className="font-semibold text-slate-700">{row.key}</span>
                    <span>{row.enabled}</span>
                    <span className="text-slate-500">{row.source}</span>
                    <span className="text-slate-400">{row.scope}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </DebugPanel>

        <DebugPanel
          title="Route & Gate Visibility"
          description="Uses existing gating rules to summarize why routes are visible or hidden."
          testId="admin-debug-routes"
        >
          <div className="space-y-4">
            <div className="rounded-lg border border-slate-100 p-4">
              <p className="text-xs font-semibold text-slate-500">Admin Routes</p>
              <div className="mt-3 space-y-2 text-xs text-slate-600">
                {routeRows.admin.map((row) => (
                  <div
                    key={row.path}
                    className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 pb-2"
                  >
                    <span className="font-semibold text-slate-700">
                      {row.label}
                    </span>
                    <span>{row.status}</span>
                    <span className="text-slate-500">{row.reason}</span>
                    <span className="text-slate-400">{row.path}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-lg border border-slate-100 p-4">
              <p className="text-xs font-semibold text-slate-500">
                Dashboard Routes
              </p>
              <div className="mt-3 space-y-2 text-xs text-slate-600">
                {routeRows.dashboard.map((row) => (
                  <div
                    key={row.path}
                    className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 pb-2"
                  >
                    <span className="font-semibold text-slate-700">
                      {row.label}
                    </span>
                    <span>{row.status}</span>
                    <span className="text-slate-500">{row.reason}</span>
                    <span className="text-slate-400">{row.path}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </DebugPanel>

        <DebugPanel
          title="UI Runtime State"
          description="Passive runtime hints for recent UI state."
          testId="admin-debug-runtime"
        >
          <EmptyState
            title="No runtime state tracked"
            message="Last normalized errors and successful actions are not tracked in UI state."
            testId="admin-debug-empty-runtime"
          />
        </DebugPanel>
      </div>
    </AdminLayout>
  );
}
